import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, X, ShoppingCart, PackageSearch, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/lib/cart-context';
import { RequestProductDialog } from '@/components/products/RequestProductDialog';
import { QuantityStepper } from '@/components/products/QuantityStepper';
import { formatTND } from '@/lib/format';
import { useProPrefs } from '@/lib/user-prefs';
import { useProArticles, useProArticlePricing } from '@/hooks/use-pro-articles';
import { useClientMode, useModePath } from '@/lib/client-mode';
import {
  calculateProLineTotals,
  resolveTierLinePricing,
  type ProArticle,
  type ProArticlePricing,
  type RemiseTier,
} from '@erp/domain/pro';

// Static keys so the i18n audit tooling sees them (no template-built keys).
const TIERS: { value: RemiseTier; labelKey: string; label: string }[] = [
  { value: 'min', labelKey: 'pro.cart.tier_min', label: 'Min' },
  { value: 'moy', labelKey: 'pro.cart.tier_moy', label: 'Moy' },
  { value: 'max', labelKey: 'pro.cart.tier_max', label: 'Max' },
];

export function CartDrawer() {
  const { t } = useTranslation();
  const {
    items,
    drawerOpen,
    setDrawerOpen,
    totals,
    tier,
    commissionRate,
    applyTier,
    updateQuantity,
    removeItem,
    requests,
    updateRequest,
    removeRequest,
    hasRequests,
  } = useCart();
  const [requestOpen, setRequestOpen] = useState(false);
  const navigate = useNavigate();
  const clientMode = useClientMode();
  const modePath = useModePath();
  // Commission visible par défaut ; masquable depuis Paramètres → Options.
  // On n'affiche la section que si le compte a un régime de commission
  // (taux admin > 0) — sinon il n'y a rien à montrer.
  //
  // En mode client, elle disparaît quelle que soit la préférence : l'écran est
  // tourné vers le client, ce qu'y gagne l'électricien ne le regarde pas.
  const { showCommission } = useProPrefs();
  const commissionVisible = !clientMode && showCommission && commissionRate > 0;
  // Set when the resync below actually rewrote line prices, so the drawer
  // can tell the user their totals changed (rather than changing silently).
  const [repriced, setRepriced] = useState(false);

  // Catalog + pricing so the tier switch can re-derive every line's
  // unitHt/remisePct (same query keys as /home, so usually a cache hit).
  // Only fetched when there is something to reprice (a persisted cart) or
  // the drawer is open — an empty cart on any other page fetches nothing.
  const catalogNeeded = drawerOpen || items.length > 0;
  const articlesQuery = useProArticles({ enabled: catalogNeeded });
  const pricingQuery = useProArticlePricing({ enabled: catalogNeeded });

  // BOTH datasets must be present before any repricing: articles without
  // pricing would make resolveTierLinePricing fall back to the full base
  // price and silently strip every tier discount from the cart.
  const catalogReady =
    articlesQuery.data !== undefined && pricingQuery.data !== undefined;

  const articleById = useMemo(() => {
    const m = new Map<string, ProArticle>();
    for (const a of articlesQuery.data ?? []) m.set(a.id, a);
    return m;
  }, [articlesQuery.data]);

  const pricingByArticle = useMemo(() => {
    const m = new Map<string, ProArticlePricing | null>();
    for (const p of pricingQuery.data ?? []) m.set(p.articleId, p);
    return m;
  }, [pricingQuery.data]);

  // Keep line prices honest with the displayed tier. Two drift sources:
  //  1. On reload the tier resets to the admin-assigned base while persisted
  //     lines keep the prices of whatever tier was active when added.
  //  2. Catalog prices may have changed since an item entered the cart — the
  //     server-side price guard validates against CURRENT prices at submit.
  // Also drops lines whose article left the published catalog (their pricing
  // row was deleted) — those can never pass the server guard again.
  // Re-pricing converges in one pass (applyTier writes values that this
  // check then finds clean), so the effect can't loop.
  useEffect(() => {
    if (!catalogReady || items.length === 0) return;
    const unpublished = items.filter((it) => !articleById.has(it.articleId));
    if (unpublished.length > 0) {
      for (const it of unpublished) removeItem(it.articleId);
      setRepriced(true);
      return; // next run (post-removal) handles any remaining staleness
    }
    const stale = items.some((it) => {
      const article = articleById.get(it.articleId);
      if (!article) return false;
      const lp = resolveTierLinePricing(
        article,
        pricingByArticle.get(it.articleId) ?? null,
        tier,
      );
      return (
        lp.unitHt !== it.unitHt ||
        lp.remisePct !== it.remisePct ||
        article.vatRate !== it.vatRate ||
        article.purchasePrice !== it.purchasePrice
      );
    });
    if (stale) {
      applyTier(tier, pricingByArticle, articleById);
      setRepriced(true);
    }
  }, [
    catalogReady,
    items,
    tier,
    articleById,
    pricingByArticle,
    applyTier,
    removeItem,
  ]);

  if (!drawerOpen) return null;

  const isEmpty = items.length === 0 && requests.length === 0;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 z-40 pro-fade-in"
        onClick={() => setDrawerOpen(false)}
        aria-hidden="true"
      />
      <aside className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white shadow-2xl z-50 flex flex-col pro-slide-in-right">
        <div
          className="h-16 px-5 flex items-center justify-between"
          style={{ borderBottom: '1px solid var(--pro-line)' }}
        >
          <h2
            className="text-[18px] font-semibold"
            style={{ fontFamily: 'Figtree', color: 'var(--pro-text-dark)' }}
          >
            {t('pro.cart.title', 'Mon panier')} ({items.length + requests.length})
          </h2>
          <button
            type="button"
            aria-label={t('pro.cart.close', 'Fermer')}
            onClick={() => setDrawerOpen(false)}
            className="w-9 h-9 rounded-lg hover:bg-neutral-100 flex items-center justify-center"
          >
            <X className="w-5 h-5" style={{ color: 'var(--pro-text-dark)' }} />
          </button>
        </div>

        {isEmpty ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-3">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ background: 'var(--pro-grey-subtle)' }}
            >
              <ShoppingCart
                className="w-7 h-7"
                style={{ color: 'var(--pro-text-light)' }}
              />
            </div>
            <p style={{ color: 'var(--pro-text-grey)' }}>
              {t('pro.cart.empty', 'Panier vide')}
            </p>
            <button
              type="button"
              onClick={() => setRequestOpen(true)}
              className="mt-1 inline-flex items-center gap-1.5 text-[13px] font-semibold hover:underline"
              style={{ color: 'var(--pro-primary)' }}
            >
              <PackageSearch className="w-4 h-4" />
              {t('pro.request.cta', 'Demander un produit')}
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {items.map((item) => {
                const unitTtc = calculateProLineTotals({
                  quantity: 1,
                  unitHt: item.unitHt,
                  remisePct: item.remisePct,
                  vatRate: item.vatRate,
                }).pttc;
                return (
                  <div
                    key={item.articleId}
                    className="flex gap-3 p-3 rounded-2xl"
                    style={{ border: '1px solid var(--pro-line)' }}
                  >
                    <div
                      className="w-16 h-16 rounded-xl overflow-hidden shrink-0"
                      style={{ background: 'var(--pro-grey-subtle)' }}
                    >
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-[14px] font-medium truncate"
                        style={{
                          color: 'var(--pro-text-dark)',
                          fontFamily: 'Figtree',
                        }}
                      >
                        {item.name}
                      </div>
                      <div
                        className="text-[12px] mt-0.5"
                        style={{ color: 'var(--pro-text-grey)' }}
                      >
                        {item.code}
                      </div>
                      <div
                        className="text-[14px] font-semibold mt-1"
                        style={{
                          color: 'var(--pro-text-dark)',
                          fontFamily: 'Figtree',
                        }}
                      >
                        {item.quantity} × {formatTND(unitTtc)} TTC
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <QuantityStepper
                          variant="pill"
                          value={item.quantity}
                          onChange={(n) => updateQuantity(item.articleId, n)}
                          decreaseLabel={t('pro.cart.decrease', 'Diminuer')}
                          increaseLabel={t('pro.cart.increase', 'Augmenter')}
                          inputLabel={t('pro.cart.quantity', 'Quantité')}
                        />
                        <button
                          type="button"
                          aria-label={t('pro.cart.remove', 'Retirer')}
                          onClick={() => removeItem(item.articleId)}
                          className="ml-auto p-1.5 rounded-md hover:bg-red-50"
                          style={{ color: 'var(--pro-danger)' }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Produits hors catalogue. Bordure pointillée + fond orange
                  pâle : ce ne sont pas des lignes chiffrées, et rien dans les
                  totaux ne les représente — la carte doit le dire d'elle-même. */}
              {requests.length > 0 && (
                <div className="pt-1 space-y-3">
                  <div className="flex items-center gap-2">
                    <PackageSearch
                      className="w-4 h-4 shrink-0"
                      style={{ color: 'var(--pro-primary-text)' }}
                    />
                    <span
                      className="text-[13px] font-semibold"
                      style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                    >
                      {t('pro.request.section', 'Produits demandés')} ({requests.length})
                    </span>
                  </div>

                  {requests.map((request) => (
                    <div
                      key={request.id}
                      className="p-3 rounded-2xl"
                      style={{
                        border: '1px dashed var(--pro-primary-light)',
                        background: 'var(--pro-primary-subtle)',
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div
                            className="text-[14px] font-medium break-words"
                            style={{
                              color: 'var(--pro-text-dark)',
                              fontFamily: 'Figtree',
                            }}
                          >
                            {request.label}
                          </div>
                          {request.note && (
                            <div
                              className="text-[12px] mt-0.5 break-words"
                              style={{ color: 'var(--pro-text-grey)' }}
                            >
                              {request.note}
                            </div>
                          )}
                          <div
                            className="text-[12px] font-medium mt-1"
                            style={{ color: 'var(--pro-primary-text)' }}
                          >
                            {t('pro.request.price_pending', 'Prix à confirmer')}
                          </div>
                        </div>
                        <button
                          type="button"
                          aria-label={t('pro.cart.remove', 'Retirer')}
                          onClick={() => removeRequest(request.id)}
                          className="p-1.5 rounded-md hover:bg-white/70 shrink-0"
                          style={{ color: 'var(--pro-danger)' }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="mt-2">
                        <QuantityStepper
                          variant="pill"
                          className="bg-white"
                          value={request.quantity}
                          onChange={(n) =>
                            updateRequest(request.id, { quantity: Math.max(1, n) })
                          }
                          min={1}
                          decreaseLabel={t('pro.cart.decrease', 'Diminuer')}
                          increaseLabel={t('pro.cart.increase', 'Augmenter')}
                          inputLabel={t('pro.cart.quantity', 'Quantité')}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => setRequestOpen(true)}
                className="w-full inline-flex items-center justify-center gap-1.5 h-11 rounded-xl text-[13px] font-semibold transition-colors hover:bg-neutral-50"
                style={{
                  border: '1px dashed var(--pro-border)',
                  color: 'var(--pro-primary)',
                  fontFamily: 'Figtree',
                }}
              >
                <Plus className="w-4 h-4" />
                {t('pro.request.cta', 'Demander un produit')}
              </button>
            </div>

            <div
              className="p-4 space-y-3"
              style={{ borderTop: '1px solid var(--pro-line)' }}
            >
              {/* Palier de remise : c'est la remise de L'ÉLECTRICIEN. Le
                  sélecteur n'est pas rendu du tout en mode client — le palier
                  actif reste appliqué aux prix, simplement il ne se nomme plus.
                  Pas rendu plutôt que masqué en CSS : rien à trouver dans le
                  DOM devant le client. */}
              {!clientMode && (
              <div className="flex items-center justify-between gap-2">
                <span
                  className="text-[13px] shrink-0"
                  style={{ color: 'var(--pro-text-grey)' }}
                >
                  {t('pro.cart.tier', 'Remise')}
                </span>
                <div
                  className="inline-flex items-center gap-1"
                  role="group"
                  aria-label={t('pro.cart.tier', 'Remise')}
                >
                  {TIERS.map((opt) => {
                    const active = opt.value === tier;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        aria-pressed={active}
                        // Inert until the catalog is loaded: switching needs
                        // both maps to re-derive prices — flipping the label
                        // without repricing would desync typeRemise from the
                        // line prices sent at checkout.
                        disabled={!catalogReady}
                        // applyTier re-runs resolveTierLinePricing on every
                        // line so prices, totals and commission update
                        // immediately — setTier alone would only move the
                        // highlight while the prices stayed snapshotted.
                        // `explicit` persists the choice across reloads.
                        onClick={() => {
                          if (!catalogReady) return;
                          setRepriced(false);
                          applyTier(opt.value, pricingByArticle, articleById, {
                            explicit: true,
                          });
                        }}
                        className={`inline-flex items-center justify-center h-7 px-2.5 rounded-lg text-[12px] font-semibold select-none transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                          active ? '' : 'hover:bg-neutral-100'
                        }`}
                        style={
                          active
                            ? {
                                background: 'var(--pro-primary-subtle)',
                                color: 'var(--pro-primary-text)',
                                border: '1px solid var(--pro-primary-light)',
                              }
                            : {
                                color: 'var(--pro-text-muted)',
                                border: '1px solid transparent',
                              }
                        }
                      >
                        {t(opt.labelKey, opt.label)}
                      </button>
                    );
                  })}
                </div>
              </div>
              )}

              {repriced && (
                <div
                  className="text-[11px] rounded-lg px-3 py-2"
                  style={{
                    color: 'var(--pro-text-grey)',
                    background: 'var(--pro-grey-subtle)',
                  }}
                >
                  {t(
                    'pro.cart.prices_updated',
                    'Les prix ont été actualisés selon le catalogue.',
                  )}
                </div>
              )}

              <div
                className="rounded-2xl p-4 space-y-2"
                style={{
                  background: 'var(--pro-primary-subtle)',
                  border: '1px solid var(--pro-primary-light)',
                }}
              >
                <div
                  className="flex justify-between text-[13px]"
                  style={{ color: 'var(--pro-text-grey)' }}
                >
                  <span>{t('pro.cart.subtotal_ht', 'Sous-total HT')}</span>
                  <span>{formatTND(totals.pht)}</span>
                </div>
                <div
                  className="flex justify-between text-[13px]"
                  style={{ color: 'var(--pro-text-grey)' }}
                >
                  <span>{t('pro.cart.tva', 'TVA')}</span>
                  <span>{formatTND(totals.tva)}</span>
                </div>
                <div
                  className="flex justify-between text-[16px] font-bold pt-2"
                  style={{
                    color: 'var(--pro-text-dark)',
                    fontFamily: 'Figtree',
                    borderTop: '1px solid rgba(255,107,30,0.2)',
                  }}
                >
                  <span>{t('pro.cart.total_ttc', 'Total TTC')}</span>
                  <span>{formatTND(totals.pttc)}</span>
                </div>
                {commissionVisible ? (
                  <div
                    className="flex justify-between text-[12px] font-medium"
                    style={{ color: 'var(--pro-primary)' }}
                  >
                    <span>{t('pro.cart.commission', 'Commission estimée')}</span>
                    <span>{formatTND(totals.totalCommission)}</span>
                  </div>
                ) : null}
                {commissionVisible && totals.totalCommission > 0 ? (
                  <div
                    className="text-[11px]"
                    style={{ color: 'var(--pro-text-grey)' }}
                  >
                    {t(
                      'pro.cart.commission_pending_hint',
                      'Disponible après validation de la commande.',
                    )}
                  </div>
                ) : null}
              </div>

              {/* Une demande n'a pas de prix : on ne peut pas encaisser un
                  panier dont une partie n'est pas chiffrée. Le devis devient
                  donc la seule sortie, et le bouton devient l'action
                  principale au lieu d'un choix parmi deux. */}
              {hasRequests ? (
                <>
                  <div
                    className="text-[12px] rounded-xl px-3 py-2.5 leading-relaxed"
                    style={{
                      background: 'var(--pro-primary-subtle)',
                      color: 'var(--pro-primary-text)',
                      border: '1px solid var(--pro-primary-light)',
                    }}
                  >
                    {t(
                      'pro.request.devis_only',
                      'Votre panier contient un produit à chiffrer : il part en devis. Vous recevrez une réponse pour chaque produit demandé.',
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setDrawerOpen(false);
                      navigate(`${modePath('/checkout')}?mode=devis`);
                    }}
                    className="w-full px-4 h-12 rounded-xl text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
                    style={{
                      background: 'var(--pro-primary)',
                      fontFamily: 'Figtree',
                    }}
                  >
                    {t('pro.cart.place_devis', 'Placer un devis')}
                  </button>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setDrawerOpen(false);
                      navigate(`${modePath('/checkout')}?mode=devis`);
                    }}
                    className="px-4 h-12 rounded-xl text-[14px] font-semibold transition-colors hover:bg-neutral-50"
                    style={{
                      border: '1px solid var(--pro-border)',
                      color: 'var(--pro-text-dark)',
                      fontFamily: 'Figtree',
                    }}
                  >
                    {t('pro.cart.place_devis', 'Placer un devis')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDrawerOpen(false);
                      navigate(`${modePath('/checkout')}?mode=commande`);
                    }}
                    className="px-4 h-12 rounded-xl text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
                    style={{
                      background: 'var(--pro-primary)',
                      fontFamily: 'Figtree',
                    }}
                  >
                    {t('pro.cart.place_commande', 'Passer commande')}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </aside>

      <RequestProductDialog
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
      />
    </>
  );
}
